import { FlaskConical, FileText, Microscope } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    labName: string;
    labNhcId?: string;
    sampleId: string;
    sampleType: string;
    collectedAt: string;
    reportedAt: string;
    tests: {
      name: string;
      result: string;
      unit: string;
      referenceRange: string;
      flag?: string;
    }[];
    interpretation?: string;
    methodology?: string;
    signatories: {
      name: string;
      role: string;
      nhcId?: string;
      licenseNo?: string;
      institution?: string;
    }[];
  };
}

function getFlagStyle(flag?: string) {
  if (!flag) return '';
  const upper = flag.toUpperCase();
  if (upper === 'HIGH') return 'text-red-600 font-bold';
  if (upper === 'LOW') return 'text-blue-600 font-bold';
  return 'text-yellow-600 font-semibold';
}

export default function LabReportTemplate({ data }: Props) {
  return (
    <div className="space-y-3">
      {/* Lab Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="w-4 h-4 text-gray-700" />
          <span className="text-xs font-bold uppercase tracking-wider text-gray-700">
            Laboratory Report
          </span>
        </div>
        <div className="text-right text-[10px] text-gray-600">
          <p className="font-semibold text-gray-800">{data.labName}</p>
          {data.labNhcId && <p className="font-mono">{data.labNhcId}</p>}
        </div>
      </div>

      {/* Sample Information */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[10px]">
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Sample ID:</span>
          <span className="text-gray-800 font-mono">{data.sampleId}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Sample Type:</span>
          <span className="text-gray-800">{data.sampleType}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Collected:</span>
          <span className="text-gray-800">{data.collectedAt}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold text-gray-600">Reported:</span>
          <span className="text-gray-800">{data.reportedAt}</span>
        </div>
      </div>

      {/* Test Results Table */}
      <div>
        <div className="flex items-center gap-1.5 mb-2">
          <FileText className="w-3 h-3 text-gray-600" />
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
            Test Results
          </h3>
        </div>
        <table className="w-full border-collapse text-[10px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Test
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Result
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Unit
              </th>
              <th className="text-left font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Reference Range
              </th>
              <th className="text-center font-bold text-gray-700 py-1 px-1.5 border border-gray-300">
                Flag
              </th>
            </tr>
          </thead>
          <tbody>
            {data.tests.map((test, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="py-1 px-1.5 border border-gray-200 font-medium text-gray-800">
                  {test.name}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-800 font-mono">
                  {test.result}
                </td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-600">{test.unit}</td>
                <td className="py-1 px-1.5 border border-gray-200 text-gray-600">
                  {test.referenceRange}
                </td>
                <td
                  className={`py-1 px-1.5 border border-gray-200 text-center ${getFlagStyle(test.flag)}`}
                >
                  {test.flag ? test.flag.toUpperCase() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Interpretation */}
      {data.interpretation && (
        <div>
          <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600 mb-1">
            Interpretation
          </h3>
          <p className="text-[10px] text-gray-700 leading-relaxed pl-1">{data.interpretation}</p>
        </div>
      )}

      {/* Methodology */}
      {data.methodology && (
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <Microscope className="w-3 h-3 text-gray-600" />
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-gray-600">
              Methodology
            </h3>
          </div>
          <p className="text-[10px] text-gray-600 italic leading-relaxed pl-1">
            {data.methodology}
          </p>
        </div>
      )}

      {/* Digital Signatures */}
      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
